import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { updateConsultationAsync, clearError, Consultation } from '../store/slices/consultationsSlice';
import { fetchStudentsForDropdownAsync } from '../store/slices/certificatesSlice';
import { fetchGroupsAsync } from '../store/slices/groupsSlice';
import { useLevels } from '../hooks/useLevels';
import { fetchAdsAsync } from '../store/slices/adsSlice';
import LoadingSpinner from './LoadingSpinner';
import Select, { SelectOption } from './Select';
import ConsultationRecordingsSection from './ConsultationRecordingsSection';
import ConsultationAIInsightsSection from './ConsultationAIInsightsSection';
import ConsultationSellingReviewSection from './ConsultationSellingReviewSection';
import ConsultationStatusHistory from './ConsultationStatusHistory';
import ConsultationLostReasonField from './ConsultationLostReasonField';
import ConfirmationModal from './ConfirmationModal';
import StudentReferrerPicker, { formatStudentName, type ReferrerOption } from './StudentReferrerPicker';
import RewardSubscriptionBox from './RewardSubscriptionBox';
import {
  STATUS_LABELS,
  allowedTargets,
  type ConsultationStatus,
} from '../constants/consultationStatus';
import axios from 'axios';
import { getApiErrorMessage } from '../utils/apiError';
import './GroupForm.css';

// Shape sent to PUT /consultations/:id. Mirrors the editable subset of
// Consultation, but every field is nullable here because the form serializes
// empty inputs to null.
type ConsultationUpdatePayload = {
  firstName: string | null;
  lastName: string | null;
  patronymic: string | null;
  contact: string | null;
  firstContactDate: string | null;
  consultationDate: string;
  consultationTime: string | null;
  isOrganic: boolean;
  status: string | null;
  langLevel: string | null;
  studentId: number | null;
  suggestedGroupId: number | null;
  referrerStudentId: number | null;
  commentary: string | null;
  lostReason: string | null;
  lostReasonOther: string | null;
  adId?: number | null;
};

const EditConsultation: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error } = useAppSelector((state) => state.consultations);
  const { ads } = useAppSelector((state) => state.ads);
  const { token } = useAppSelector((state) => state.auth);
  const studentsForDropdown = useAppSelector((state) => state.certificates.studentsForDropdown);
  const { groups } = useAppSelector((state) => state.groups);
  const levels = useLevels();

  const [consultationData, setConsultationData] = useState<Consultation | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    patronymic: '',
    contact: '',
    firstContactDate: '',
    consultationDate: '',
    consultationTime: '',
    adId: '',
    isOrganic: false,
    status: '',
    langLevel: '',
    studentId: '' as string | number,
    suggestedGroupId: '' as string | number,
    commentary: '',
    lostReason: '',
    lostReasonOther: '',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isLoadingConsultation, setIsLoadingConsultation] = useState(true);

  // Delete-confirmation state. Two phases: first confirm with count, then proceed to cascade delete.
  const [deleteState, setDeleteState] = useState<
    | { phase: 'idle' }
    | { phase: 'confirm'; recordingCount: number }
    | { phase: 'deleting' }
  >({ phase: 'idle' });
  const [deleteError, setDeleteError] = useState<string>('');
  const [referrerOption, setReferrerOption] = useState<ReferrerOption | null>(null);

  const handleRequestDelete = async () => {
    setDeleteError('');
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL as string;
      const res = await axios.get(`${API_BASE_URL}/consultations/${id}/recordings/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeleteState({ phase: 'confirm', recordingCount: res.data.count || 0 });
    } catch (err) {
      setDeleteError(getApiErrorMessage(err, 'Failed to check recordings count'));
    }
  };

  const handleConfirmDelete = async () => {
    if (deleteState.phase !== 'confirm') return;
    setDeleteState({ phase: 'deleting' });
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL as string;
      const url = deleteState.recordingCount > 0
        ? `${API_BASE_URL}/consultations/${id}?cascadeRecordings=true`
        : `${API_BASE_URL}/consultations/${id}`;
      await axios.delete(url, { headers: { Authorization: `Bearer ${token}` } });
      navigate('/consultations');
    } catch (err) {
      setDeleteError(getApiErrorMessage(err, 'Failed to delete consultation'));
      setDeleteState({ phase: 'idle' });
    }
  };

  useEffect(() => {
    dispatch(fetchAdsAsync({ page: 1, limit: 1000 }));
    dispatch(fetchStudentsForDropdownAsync());
    dispatch(fetchGroupsAsync({ page: 1, limit: 200 }));
  }, [dispatch]);

  // Fetch consultation data
  const fetchConsultation = useCallback(async () => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL as string;
      const response = await axios.get(`${API_BASE_URL}/consultations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const consultation = response.data.consultation;
      setConsultationData(consultation);
      setFormData({
        firstName: consultation.firstName || '',
        lastName: consultation.lastName || '',
        patronymic: consultation.patronymic || '',
        contact: consultation.contact || '',
        firstContactDate: consultation.firstContactDate || '',
        consultationDate: consultation.consultationDate || '',
        consultationTime: consultation.consultationTime ? consultation.consultationTime.slice(0, 5) : '',
        adId: consultation.adId?.toString() || '',
        isOrganic: consultation.isOrganic,
        status: consultation.status || '',
        langLevel: consultation.langLevel || '',
        studentId: consultation.studentId?.toString() || '',
        suggestedGroupId: consultation.suggestedGroupId?.toString() || '',
        commentary: consultation.commentary || '',
        lostReason: consultation.lostReason || '',
        lostReasonOther: consultation.lostReasonOther || '',
      });
      if (consultation.referrer) {
        setReferrerOption({
          value: consultation.referrer.id,
          label: formatStudentName(consultation.referrer),
          student: consultation.referrer,
        });
      } else {
        setReferrerOption(null);
      }
      setIsLoadingConsultation(false);
    } catch (error) {
      console.error('Failed to fetch consultation:', error);
      setIsLoadingConsultation(false);
    }
  }, [id, token]);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      await fetchConsultation();
    })();
  }, [id, token, fetchConsultation]);

  // Filter active ads (memoized to avoid unnecessary recalculations)
  const activeAds = useMemo(() => {
    return ads.filter(ad => {
      const endDate = new Date(ad.dateEnd);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return endDate >= today;
    });
  }, [ads]);

  // Separate expired ads
  const expiredAds = useMemo(() => {
    return ads.filter(ad => {
      const endDate = new Date(ad.dateEnd);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return endDate < today;
    });
  }, [ads]);

  const studentOptions = useMemo<SelectOption<number>[]>(() => {
    return (studentsForDropdown || []).map((s) => ({
      value: s.id,
      label: [s.firstName, s.lastName].filter(Boolean).join(' ') || `Student #${s.id}`,
    }));
  }, [studentsForDropdown]);

  const selectedStudent = useMemo<SelectOption<number> | null>(() => {
    if (!formData.studentId) return null;
    const id = typeof formData.studentId === 'string' ? parseInt(formData.studentId, 10) : formData.studentId;
    return studentOptions.find(o => o.value === id) || null;
  }, [formData.studentId, studentOptions]);

  const groupOptions = useMemo<SelectOption<number>[]>(() => {
    return (groups || []).map(g => ({
      value: g.id,
      label: `${g.name || 'Unnamed'} (${g.level.toUpperCase()})`,
    }));
  }, [groups]);

  const selectedGroup = useMemo<SelectOption<number> | null>(() => {
    if (!formData.suggestedGroupId) return null;
    const id = typeof formData.suggestedGroupId === 'string' ? parseInt(formData.suggestedGroupId, 10) : formData.suggestedGroupId;
    return groupOptions.find(o => o.value === id) || null;
  }, [formData.suggestedGroupId, groupOptions]);

  // Status options reflect the legal transitions from the *persisted* status
  // (scheduled stays available only if currently scheduled or unset).
  const statusOptions = useMemo<SelectOption<ConsultationStatus>[]>(() => {
    return allowedTargets(consultationData?.status ?? null).map(s => ({ value: s, label: STATUS_LABELS[s] }));
  }, [consultationData?.status]);

  const selectedStatus = useMemo<SelectOption<ConsultationStatus> | null>(() => {
    return statusOptions.find(o => o.value === formData.status) || null;
  }, [statusOptions, formData.status]);
  const isReferralSource = referrerOption !== null;

  const handleReferrerChange = (option: ReferrerOption | null) => {
    setReferrerOption(option);
    if (option) {
      setFormData(prev => ({ ...prev, isOrganic: false, adId: '' }));
      setFormErrors(prev => ({ ...prev, adId: '' }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked,
        // Clear adId when marking as organic
        ...(name === 'isOrganic' && checked ? { adId: '' } : {})
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear form error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.consultationDate) {
      errors.consultationDate = 'Consultation date is required';
    }

    if (!formData.status) {
      errors.status = 'Status is required';
    }

    if (!isReferralSource && !formData.isOrganic && !formData.adId) {
      errors.adId = 'Ad is required for non-organic consultations';
    }

    if (formData.status === 'not_interested' && formData.lostReason === 'other' && !formData.lostReasonOther.trim()) {
      errors.lostReasonOther = 'A note is required when the reason is "Other"';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any previous errors
    if (error) dispatch(clearError());
    
    if (!validateForm()) {
      return;
    }

    const consultationData: ConsultationUpdatePayload = {
      firstName: formData.firstName.trim() || null,
      lastName: formData.lastName.trim() || null,
      patronymic: formData.patronymic.trim() || null,
      contact: formData.contact.trim() || null,
      firstContactDate: formData.firstContactDate || null,
      consultationDate: formData.consultationDate,
      consultationTime: formData.consultationTime || null,
      isOrganic: isReferralSource ? false : formData.isOrganic,
      status: formData.status || null,
      langLevel: formData.langLevel || null,
      studentId: formData.studentId ? parseInt(formData.studentId as string, 10) : null,
      suggestedGroupId: formData.suggestedGroupId ? parseInt(formData.suggestedGroupId as string, 10) : null,
      referrerStudentId: referrerOption ? referrerOption.value : null,
      commentary: formData.commentary.trim() || null,
      lostReason: formData.status === 'not_interested' ? (formData.lostReason || null) : null,
      lostReasonOther: formData.status === 'not_interested' ? (formData.lostReasonOther.trim() || null) : null,
    };

    // Only include adId if not organic
    if (isReferralSource || formData.isOrganic) {
      consultationData.adId = null;
    } else {
      consultationData.adId = formData.adId ? parseInt(formData.adId, 10) : null;
    }

    try {
      await dispatch(updateConsultationAsync({ id: parseInt(id!), data: consultationData as Partial<Consultation> })).unwrap();
      // Navigate back to consultations list
      navigate('/consultations');
    } catch (error) {
      // Error is handled by the slice
    }
  };

  const handleCancel = () => {
    navigate('/consultations');
  };

  if (isLoadingConsultation || loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="group-form-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Edit Consultation</h1>
          <p>Update consultation record</p>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="consultation-edit">
        <section className="consultation-edit__card">
          <h2 className="consultation-edit__card-title">Client Information</h2>

          <div className="form-row-compact three-columns">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="Enter first name" className="form-input" />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="Enter last name" className="form-input" />
            </div>
            <div className="form-group">
              <label htmlFor="patronymic">Patronymic</label>
              <input type="text" id="patronymic" name="patronymic" value={formData.patronymic} onChange={handleInputChange} placeholder="Enter patronymic" className="form-input" />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="contact">Contact</label>
            <input type="text" id="contact" name="contact" value={formData.contact} onChange={handleInputChange} placeholder="Phone, email, or other contact" className="form-input" />
          </div>

        </section>

        <section className="consultation-edit__card">
          <h2 className="consultation-edit__card-title">Acquisition Source</h2>

          <div className="form-group">
            <label>Brought by</label>
            <StudentReferrerPicker
              value={referrerOption}
              onChange={handleReferrerChange}
            />
          </div>

          {isReferralSource ? (
            <div className="form-group">
              <label>Source</label>
              <div className="consultation-source-locked">
                <strong>Referral</strong>
                <span>Locked while “Brought by” is set</span>
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label className="checkbox-control">
                <input type="checkbox" name="isOrganic" checked={formData.isOrganic} onChange={handleInputChange} />
                <span className="checkbox-control__label">Organic (no ad source)</span>
              </label>
            </div>
          )}

          {!isReferralSource && !formData.isOrganic && (
            <div className="form-group">
              <label htmlFor="adId">Ad *</label>
              <select id="adId" name="adId" value={formData.adId} onChange={handleInputChange} className={`form-select ${formErrors.adId ? 'error' : ''} ${!formData.adId ? 'form-select--muted' : ''}`} required={!formData.isOrganic}>
                <option value="">Select ad</option>
                {activeAds.length > 0 && (
                  <optgroup label="Active Ads">
                    {activeAds.map(ad => <option key={ad.id} value={ad.id}>{ad.name} ({ad.platform})</option>)}
                  </optgroup>
                )}
                {expiredAds.length > 0 && (
                  <optgroup label="Expired Ads">
                    {expiredAds.map(ad => <option key={ad.id} value={ad.id}>{ad.name} ({ad.platform})</option>)}
                  </optgroup>
                )}
              </select>
              {formErrors.adId && <span className="error-text">{formErrors.adId}</span>}
            </div>
          )}
        </section>

        <section className="consultation-edit__card">
          <h2 className="consultation-edit__card-title">Consultation Details</h2>

          <div className="field-grid">
            <div className="form-group span-3">
              <label htmlFor="firstContactDate">First Contact Date</label>
              <input type="date" id="firstContactDate" name="firstContactDate" value={formData.firstContactDate} onChange={handleInputChange} className={`form-input ${formErrors.firstContactDate ? 'error' : ''}`} />
            </div>

            <div className="form-group span-3">
              <label htmlFor="consultationDate">Consultation Date *</label>
              <input type="date" id="consultationDate" name="consultationDate" value={formData.consultationDate} onChange={handleInputChange} className={`form-input ${formErrors.consultationDate ? 'error' : ''}`} required />
            </div>

            <div className="form-group span-2">
              <label htmlFor="consultationTime">Consultation Time</label>
              <input type="time" id="consultationTime" name="consultationTime" value={formData.consultationTime} onChange={handleInputChange} className="form-input" />
            </div>

            <div className="form-group span-4">
              <label>Status *</label>
              <Select<ConsultationStatus, SelectOption<ConsultationStatus>>
                value={selectedStatus}
                onChange={(opt) => setFormData(prev => ({
                  ...prev,
                  status: opt ? opt.value : '',
                  // Reason only applies to not_interested — drop it otherwise.
                  ...(opt?.value === 'not_interested' ? {} : { lostReason: '', lostReasonOther: '' }),
                }))}
                options={statusOptions}
                placeholder="Select status…"
                isClearable={false}
                isError={!!formErrors.status}
              />
              {formErrors.status && <span className="error-text">{formErrors.status}</span>}
            </div>

            {formData.status === 'not_interested' && (
              <ConsultationLostReasonField
                lostReason={formData.lostReason}
                lostReasonOther={formData.lostReasonOther}
                noteError={formErrors.lostReasonOther}
                onChange={({ lostReason, lostReasonOther }) => {
                  setFormData(prev => ({ ...prev, lostReason, lostReasonOther }));
                  if (formErrors.lostReasonOther) setFormErrors(prev => ({ ...prev, lostReasonOther: '' }));
                }}
              />
            )}

            <div className="form-group span-3">
              <label htmlFor="langLevel">Language Level</label>
              <select id="langLevel" name="langLevel" value={formData.langLevel} onChange={handleInputChange} className={`form-select ${!formData.langLevel ? 'form-select--muted' : ''}`}>
                <option value="">Not set</option>
                {levels.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>

            <div className="form-group span-5">
              <label>Linked Student</label>
              <Select<number, SelectOption<number>>
                value={selectedStudent}
                onChange={(opt) => setFormData(prev => ({ ...prev, studentId: opt ? opt.value.toString() : '' }))}
                options={studentOptions}
                placeholder="— None —"
                isClearable
                noOptionsMessage="No students"
              />
            </div>

            <div className="form-group span-4">
              <label>Suggested Group</label>
              <Select<number, SelectOption<number>>
                value={selectedGroup}
                onChange={(opt) => setFormData(prev => ({ ...prev, suggestedGroupId: opt ? opt.value.toString() : '' }))}
                options={groupOptions}
                placeholder="— None —"
                isClearable
                noOptionsMessage="No groups"
              />
            </div>

            <div className="form-group span-12">
              <label htmlFor="commentary">Note</label>
              <textarea id="commentary" name="commentary" value={formData.commentary} onChange={handleInputChange} placeholder="Additional notes or comments" className="form-input" rows={3} />
            </div>
          </div>
        </section>

        {consultationData?.studentId && referrerOption && consultationData.pendingReferralReward && (
          <RewardSubscriptionBox
            kind="referral"
            recipientStudentId={referrerOption.value}
            recipientName={formatStudentName(referrerOption.student)}
            referredStudentId={consultationData.studentId}
            referredFirstName={consultationData.student?.firstName || consultationData.firstName || undefined}
            onSuccess={() => { void fetchConsultation(); }}
          />
        )}

        {id && (
          <ConsultationRecordingsSection
            consultationId={parseInt(id, 10)}
            actions={consultationData ? (
              <ConsultationSellingReviewSection
                consultation={consultationData}
                onRefresh={fetchConsultation}
              />
            ) : undefined}
          />
        )}

        {consultationData && (
          <ConsultationAIInsightsSection
            consultation={consultationData}
            onRefresh={fetchConsultation}
          />
        )}

        <ConsultationStatusHistory items={consultationData?.statusHistory} />

        {deleteError && (
          <div className="error-message" style={{ margin: '0 2rem' }}>{deleteError}</div>
        )}

        <div className="consultation-edit__actions">
          <button type="button" onClick={handleRequestDelete} className="btn-danger" disabled={deleteState.phase === 'deleting'}>
            Delete
          </button>
          <span style={{ flex: 1 }} />
          <button type="button" onClick={handleCancel} className="btn-secondary">Cancel</button>
          {['awaiting_payment', 'waitlist', 'paid'].includes(formData.status) && !formData.studentId && (
            <button
              type="button"
              className="btn-create-student"
              onClick={() => navigate(`/students/create?fromConsultation=${id}`)}
            >
              Create Student
            </button>
          )}
          <button type="submit" className="btn-success" disabled={loading}>
            {loading ? 'Updating...' : 'Update Consultation'}
          </button>
        </div>
      </form>

      <ConfirmationModal
        isOpen={deleteState.phase === 'confirm'}
        title="Delete consultation?"
        message={
          deleteState.phase === 'confirm' && deleteState.recordingCount > 0
            ? `This will permanently delete ${deleteState.recordingCount} recording(s) and their transcripts. Continue?`
            : 'This will permanently delete the consultation. Continue?'
        }
        confirmText="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteState({ phase: 'idle' })}
        dangerous
      />
    </div>
  );
};

export default EditConsultation;
